import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Shield, 
  AlertTriangle, 
  Activity, 
  Globe,
  Clock,
  Eye,
  User,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminHeader from '../../../shared/components/AdminHeader';
import { adminAuthService } from '../../../shared/services/adminAuth';

interface SecurityLogEntry {
  _id: string;
  type: 'threat_blocked' | 'request_blocked' | 'suspicious_activity' | 'failed_login' | 'malicious_url_blocked' | 'geo_blocked' | 'rate_limit_exceeded';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  timestamp: string;
  source: {
    ip: string;
    userAgent?: string;
    country?: string;
  };
  details?: any;
}

interface SecurityStats {
  totalLogs: number;
  criticalThreats: number;
  blockedToday: number;
  topAttackingIP: string;
  mostCommonThreat: string;
}

export default function SecurityLogsPage() {
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get('type') || 'all';
  
  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState<SecurityLogEntry[]>([]);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<SecurityLogEntry | null>(null);

  useEffect(() => {
    loadSecurityLogs();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadSecurityLogs, 30000);
    return () => clearInterval(interval);
  }, [filterType]); // Re-load when filter type changes

  const loadSecurityLogs = async () => {
    try {
      setIsLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        type: filterType,
        limit: '50',
        timeRange: '7d'
      });
      
      const [logsResponse, statsResponse] = await Promise.all([
        fetch(`/api/admin/security/logs?${params}`, {
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`
          }
        }),
        fetch('/api/admin/security/logs/stats', {
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`
          }
        })
      ]);
      
      if (!logsResponse.ok || !statsResponse.ok) {
        throw new Error('Failed to load security logs');
      }
      
      const logsData = await logsResponse.json();
      const statsData = await statsResponse.json();
      
      setLogs(logsData.data?.logs || []);
      setStats(statsData.data || null);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load security logs:', error);
      toast.error('Failed to load security logs');
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'threat_blocked':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'request_blocked':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'suspicious_activity':
        return <Eye className="h-4 w-4 text-yellow-500" />;
      case 'failed_login':
        return <User className="h-4 w-4 text-red-500" />;
      case 'malicious_url_blocked':
        return <Globe className="h-4 w-4 text-red-500" />;
      case 'geo_blocked':
        return <Globe className="h-4 w-4 text-blue-500" />;
      case 'rate_limit_exceeded':
        return <Activity className="h-4 w-4 text-purple-500" />;
      default:
        return <Shield className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPageTitle = () => {
    switch (filterType) {
      case 'threat_blocked':
        return 'Threats Blocked';
      case 'request_blocked':
        return 'Blocked Requests';
      case 'malicious_url_blocked':
        return 'Malicious URLs Blocked';
      case 'geo_blocked':
        return 'Geographic Blocks';
      case 'rate_limit_exceeded':
        return 'Rate Limit Violations';
      case 'suspicious_activity':
        return 'Suspicious Activity';
      case 'failed_login':
        return 'Failed Login Attempts';
      default:
        return 'Security Logs';
    }
  };

  const getPageDescription = () => {
    switch (filterType) {
      case 'threat_blocked':
        return 'Security threats that were successfully blocked by the system';
      case 'request_blocked':
        return 'Requests that were blocked due to security policies';
      case 'malicious_url_blocked':
        return 'Malicious URLs that were prevented from being shortened';
      case 'geo_blocked':
        return 'Requests blocked based on geographic restrictions';
      case 'rate_limit_exceeded':
        return 'Requests that exceeded rate limiting thresholds';
      case 'suspicious_activity':
        return 'Activities flagged as potentially suspicious';
      case 'failed_login':
        return 'Failed authentication attempts to the system';
      default:
        return 'Monitor security events and threats in real-time';
    }
  };

  const formatTypeLabel = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader title="Security Logs" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {getPageTitle()}
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {getPageDescription()}
              </p>
              {filterType !== 'all' && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    Filtered by: {formatTypeLabel(filterType)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
              </div>
              <button
                onClick={loadSecurityLogs}
                disabled={isLoading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Activity className="h-8 w-8 text-blue-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Logs</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalLogs}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Critical Threats</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.criticalThreats}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Shield className="h-8 w-8 text-green-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Blocked Today</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.blockedToday}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Globe className="h-8 w-8 text-purple-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Top Attacking IP</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white font-mono">{stats.topAttackingIP || 'None'}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Eye className="h-8 w-8 text-orange-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Most Common</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{stats.mostCommonThreat || 'None'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Security Events</h3>
          </div>
          <div className="overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No security logs found</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                  Security events will appear here when detected
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Message
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {logs.map((log) => (
                      <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getTypeIcon(log.type)}
                            <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                              {formatTypeLabel(log.type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                            {log.message}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(log.severity)}`}>
                            {log.severity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900 dark:text-white">
                            {log.source.ip}
                          </div>
                          {log.source.country && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {log.source.country}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {new Date(log.timestamp).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setSelectedEntry(log)}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Security Event Details
                </h3>
                <button
                  onClick={() => setSelectedEntry(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatTypeLabel(selectedEntry.type)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedEntry.message}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source IP</label>
                <p className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{selectedEntry.source.ip}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User Agent</label>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{selectedEntry.source.userAgent || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedEntry.source.country || 'Unknown'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timestamp</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{new Date(selectedEntry.timestamp).toLocaleString()}</p>
              </div>
              {selectedEntry.details && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Details</label>
                  <pre className="mt-1 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded overflow-x-auto">
                    {JSON.stringify(selectedEntry.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
